import { useEffect, useState } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import IdeaCard from '../components/IdeaCard';
import SEO from '../components/SEO';
import api, { trackPageView } from '../lib/api';

const IdeasPage = () => {
  const [ideas, setIdeas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    trackPageView('/ideas');
    fetchIdeas();
    fetchCategories();
  }, []);

  const fetchIdeas = async () => {
    try {
      const response = await api.get('/ideas');
      setIdeas(response.data);
    } catch (error) {
      console.error('Failed to fetch ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const filteredIdeas = ideas.filter((idea) => {
    const matchesSearch = 
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.tagline.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || idea.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || idea.status === selectedStatus;

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedStatus('all');
  };

  const hasFilters = searchTerm || selectedCategory !== 'all' || selectedStatus !== 'all';

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="ideas-page">
      <SEO 
        title="Browse Ideas"
        description="Explore our collection of innovative software and systems ideas. Find blockchain, AI, SaaS, and enterprise solutions ready for acquisition or investment."
        url="/ideas"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Explore Ideas
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl">
            Browse our collection of innovative software and systems concepts. 
            Find the perfect opportunity to acquire or invest.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8 p-6 rounded-xl bg-slate-900/50 border border-white/10" data-testid="ideas-filters">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <Input
              placeholder="Search ideas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
              data-testid="ideas-search-input"
            />
          </div>

          {/* Category Filter */}
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full lg:w-48 bg-slate-950/50 border-white/10 text-white" data-testid="category-filter">
              <Filter className="h-4 w-4 mr-2 text-slate-500" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all" className="text-white">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-full lg:w-48 bg-slate-950/50 border-white/10 text-white" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-white/10">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="available" className="text-white">Available</SelectItem>
              <SelectItem value="in_development" className="text-white">In Development</SelectItem>
              <SelectItem value="sold" className="text-white">Sold</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-slate-400 hover:text-white"
              data-testid="clear-filters-btn"
            >
              <X className="h-4 w-4 mr-2" />
              Clear
            </Button>
          )}
        </div>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-slate-400 text-sm">
            Showing <span className="text-white font-medium">{filteredIdeas.length}</span> ideas
            {hasFilters && ' (filtered)'}
          </p>
        </div>

        {/* Ideas Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, idx) => (
              <div key={idx} className="h-64 rounded-xl skeleton" />
            ))}
          </div>
        ) : filteredIdeas.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ideas-grid">
            {filteredIdeas.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="no-ideas-message">
            <p className="text-slate-400 text-lg mb-4">
              {hasFilters ? 'No ideas match your filters.' : 'No ideas available yet.'}
            </p>
            {hasFilters && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-white/10 text-white hover:bg-white/5"
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IdeasPage;
